import { useEffect, useState } from 'react';
import type { CallCenterOrder, CallCenterOrderDetail } from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';
import { DriverSelect } from '../delivery/DriverSelect';
import { callCenterApi, ccErrorMessage } from './api';
import { useDriverName } from './driverName';
import { useMoneyFormatter } from './hooks';

/**
 * Send a phone order out for delivery.
 *
 * One dialog, one decision that matters: the bag has left. Naming WHO took it
 * rides along on the same request rather than being a second button, because at
 * the counter they are the same moment — the driver is standing there, they
 * take it, they leave. Splitting them into "assign" and "send" would let a
 * cashier do half of it in the two seconds they have.
 *
 * THE DRIVER IS OPTIONAL. A shop where whoever is free takes the bag, or a
 * tenant that has not set its drivers up yet, still has to be able to record
 * the departure — and if it could not, the only way to keep the board honest
 * would be to invent a driver. Leaving the picker empty sends the order out
 * with nobody named, and says so on the confirm button.
 *
 * MONEY IS NOT COLLECTED HERE, and the footer says so. An unpaid order goes out
 * with the driver and is settled from the same card when they come back with
 * the cash — that is the whole reason a call-centre order can sit unpaid in
 * `out_for_delivery` and still show "Take payment".
 *
 * The picker is the till's own `DriverSelect`, deliberately: drivers are
 * `Security_Users` with RoleID 6 and there is exactly one list of them. A
 * second picker here would be a second place for the same list to go stale.
 */
export function DispatchDialog({
  order,
  onClose,
  onDispatched,
}: {
  order: CallCenterOrder | null;
  onClose: () => void;
  onDispatched: (order: CallCenterOrderDetail) => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const money = useMoneyFormatter();
  const driverName = useDriverName();
  const [driverId, setDriverId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-opening on a different order must not carry the last driver over — the
  // one mistake this dialog can make that nobody would notice until a customer
  // rings to ask where their food is.
  useEffect(() => {
    setDriverId(order?.driverId ?? null);
    setBusy(false);
  }, [order?.id, order?.driverId]);

  if (!order) return null;

  const isReassign = order.status === 'out_for_delivery';

  async function handleConfirm(): Promise<void> {
    if (!order) return;
    setBusy(true);
    try {
      // A null driver is a legitimate dispatch, not a missing field — the
      // server moves the status either way and leaves any existing driver
      // alone. See dispatchOrder in the service.
      const updated = await callCenterApi.branch.dispatch(order.id, driverId);
      onDispatched(updated);
      // The toast names the driver in the till's language too, through the same
      // helper the card and the drawer use — one dispatch must not produce
      // three spellings of the same person.
      const who = driverName(updated);
      toast(
        who
          ? t({
              en: `Order #${updated.entryNo ?? updated.receiptId} is out with ${who}`,
              ar: `الطلب #${updated.entryNo ?? updated.receiptId} خرج مع ${who}`,
            })
          : t({
              en: `Order #${updated.entryNo ?? updated.receiptId} is out for delivery`,
              ar: `الطلب #${updated.entryNo ?? updated.receiptId} خرج للتوصيل`,
            }),
        'success',
      );
      onClose();
    } catch (err) {
      toast(ccErrorMessage(err, 'Could not send this order out'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const name =
    (lang === 'ar' ? order.customerNameAr || order.customerName : order.customerName) ?? '';

  return (
    <div className="cc-modal-backdrop" onClick={onClose}>
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">
              {isReassign
                ? t({ en: 'Change driver', ar: 'تغيير السائق' })
                : t({ en: 'Send out for delivery', ar: 'إرسال للتوصيل' })}
            </div>
            <div className="cc-modal-sub">
              #{order.entryNo ?? order.receiptId} · {name}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose}>
            ×
          </button>
        </header>

        {order.addressSummary && <div className="cc-card-addr">{order.addressSummary}</div>}

        <div className="cc-settle-total">
          <span>
            {order.isPrepaid || order.paymentTypeId != null
              ? t({ en: 'Already paid', ar: 'مدفوع بالفعل' })
              : t({ en: 'To collect on delivery', ar: 'يُحصّل عند التسليم' })}
          </span>
          <strong>{money(order.totalAmount)}</strong>
        </div>

        {/*
          Not `cc-field`: that class styles every `input` under it, which would
          reach inside DriverSelect and put a bordered box around the dropdown's
          own search line. The picker brings its own chrome.
        */}
        <div className="cc-dispatch-field">
          <span>
            {t({ en: 'Driver (optional)', ar: 'السائق (اختياري)' })}
          </span>
          <DriverSelect value={driverId} onChange={setDriverId} disabled={busy} />
        </div>

        {/*
          Never disabled on a missing driver. The button says which of the two
          dispatches it is about to do, so an empty picker reads as a choice
          rather than as a form that has not been filled in.
        */}
        <button
          type="button"
          className="cc-send"
          disabled={busy}
          onClick={() => void handleConfirm()}
        >
          {busy
            ? t({ en: 'Sending…', ar: 'جارٍ الإرسال…' })
            : isReassign
              ? t({ en: 'Save driver', ar: 'حفظ السائق' })
              : driverId == null
                ? t({ en: 'Send out — no driver', ar: 'إرسال — بدون سائق' })
                : t({ en: 'Send with driver', ar: 'إرسال مع السائق' })}
        </button>

        <p className="cc-modal-foot">
          {order.isPrepaid || order.paymentTypeId != null
            ? t({
                en: 'This order is already paid. Sending it out only records who took it.',
                ar: 'هذا الطلب مدفوع بالفعل. الإرسال يسجّل فقط من استلمه.',
              })
            : t({
                en: 'No money is taken now. Use "Take payment" on the same card when the driver comes back with the cash.',
                ar: 'لا يتم تحصيل أي مبلغ الآن. استخدم «تحصيل الدفع» على البطاقة نفسها عند عودة السائق بالنقدية.',
              })}
        </p>
      </div>
    </div>
  );
}
